import axios from 'axios';
import Cookies from 'js-cookie';

/**
 * Fire-and-forget client error reporting. Errors are queued, deduped and
 * flushed in small batches to POST /api/client-logs so we can see what users
 * actually hit in the field (the reports expire server-side after 30 days).
 *
 * Uses its own bare axios instance (no interceptors) so a failing report can
 * never toast, retry-loop, or recurse back into this logger. If the user isn't
 * logged in or the server is down, entries are silently dropped — logging must
 * never make an outage noisier for the user.
 */

const BASE_URL = process.env.REACT_APP_API_URL || '';
const logApi = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
});

const MAX_QUEUE = 20;          // hard cap; oldest entries win, floods are dropped
const FLUSH_DELAY_MS = 5000;   // batch window after the first queued entry
const DEDUPE_WINDOW_MS = 60000; // identical errors within this window log once

let queue = [];
let flushTimer = null;
const recentKeys = new Map(); // dedupe key -> last logged timestamp

const flush = () => {
  flushTimer = null;
  if (queue.length === 0) return;

  const token = Cookies.get('accessToken');
  const entries = queue;
  queue = [];
  if (!token) return; // not logged in — nothing to attribute, drop

  logApi.post(
    '/client-logs',
    { entries },
    { headers: { Authorization: `Bearer ${token}` } }
  ).catch(() => {}); // never surface logging failures
};

const scheduleFlush = () => {
  if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
  }
};

// Flush whatever is pending when the tab goes to the background — on tablets
// the app is frequently swiped away right after an error.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && queue.length > 0) {
      if (flushTimer) clearTimeout(flushTimer);
      flush();
    }
  });
}

/**
 * Queue a client error report.
 * @param {object} entry
 * @param {string} entry.source  'api' | 'window' | 'promise' | 'manual'
 * @param {string} entry.message short description
 * @param {string} [entry.method] failed request method (source 'api')
 * @param {string} [entry.url]    failed request url (source 'api')
 * @param {number} [entry.status] failed request HTTP status (source 'api')
 * @param {string} [entry.detail] stack trace / response body, truncated here
 */
export const logClientError = (entry = {}) => {
  try {
    const key = `${entry.source}|${entry.message}|${entry.url || ''}|${entry.status || ''}`;
    const now = Date.now();
    const last = recentKeys.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) return;
    recentKeys.set(key, now);
    // Keep the dedupe map from growing unbounded on long-lived tablet sessions.
    if (recentKeys.size > 200) {
      for (const [k, t] of recentKeys) {
        if (now - t > DEDUPE_WINDOW_MS) recentKeys.delete(k);
      }
    }

    if (queue.length >= MAX_QUEUE) return;
    queue.push({
      ...entry,
      detail: typeof entry.detail === 'string' ? entry.detail.slice(0, 8000) : undefined,
      page: window.location.pathname,
      at: new Date().toISOString()
    });
    scheduleFlush();
  } catch (e) {
    // Logging must never throw into the app.
  }
};

/** Wire up window-level handlers for uncaught errors and promise rejections. */
export const installGlobalErrorLogging = () => {
  window.addEventListener('error', (event) => {
    // Resource load failures (img/script) fire 'error' with no error object.
    if (!event.message && !event.error) return;
    logClientError({
      source: 'window',
      message: event.message || String(event.error),
      detail: event.error?.stack
        || (event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined)
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    // Failed axios requests are already logged (with url/status) by the API
    // interceptor; logging the stray rejection too would double-count them.
    if (reason?.isAxiosError) return;
    logClientError({
      source: 'promise',
      message: reason?.message || String(reason),
      detail: reason?.stack
    });
  });
};
